import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { Eye } from 'phosphor-react'
import { LoaderBar, PaginationComponent } from 'components'
import { AppButton, AppPrimaryButton } from 'elements'
import { Checkbox } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { DesktopMode, MobileMode } from 'layout'
import { AuthService, ReportApprovalService, isTaskReadOnly } from 'utility'
import { useTaskDashboard, useTaskFullPreviewCache } from 'hooks'
import { TaskActionBoxes } from '../List'
import { TaskQuickPreview } from './TaskQuickPreview'
import { TaskStatusBadge } from './TaskStatusBadge'
import { MaintenanceTaskChip } from 'page-components/maintenance/MaintenanceTaskChip'
import { isMaintenanceTask } from 'page-components/maintenance/maintenance-utils'
import {
    DASHBOARD_STATUS_FILTER,
    DASHBOARD_STATUS_OPTIONS,
    DASHBOARD_VIEW,
    SHOW_TASK_DASHBOARD_SCORE,
    SHOW_TASK_DASHBOARD_ADMIN_SCORE,
    buildKpiTiles,
    buildStatusMixChartData,
    buildStatusQuickFilterOptions,
    buildAuditorQuickFilterOptions,
    buildCustomerQuickFilterOptions,
    getVisibleFacetQuickFilterOptions,
    formatTaskDate,
    formatTaskDateShort,
    getInitials,
    getScorePillClass,
    getStatusFilterVariant,
    getTaskAuditIdForApproval,
    getTaskCreatedByName,
    getTaskCustomerDisplay,
    getTaskIsApprovedFlag,
    getTaskTemplateName,
    getTaskTitleSubtitle,
    isDashboardAdmin,
    isTaskCompleted,
    mapDashboardTaskForEdit,
} from './task-dashboard-utils'
import {
    Avatar,
    CardActions,
    CardAuditorName,
    CardBody,
    CardMeta,
    CardProgressRow,
    BulkSelectionBar,
    CardRow1,
    CardRowLeft,
    CardSelectWrap,
    CardTemplate,
    CardTemplateCustomer,
    CardWho,
    CardsGrid,
    DashboardShell,
    DemoBar,
    DemoBarLeft,
    DemoHint,
    DonutLegend,
    DonutWrap,
    DonutChartWrap,
    DonutCenter,
    DueText,
    EmptyState,
    FilterActionsGroup,
    FilterActionsGroupMobile,
    FilterBar,
    FilterDateGroup,
    FilterDateRangeGroup,
    FilterDropdownWrap,
    FilterFieldsRow,
    FilterGroup,
    InsightsRow,
    InsightsRowMobile,
    RoleSegment,
    KpiDelta,
    KpiLabel,
    KpiRow,
    KpiRowMobile,
    KpiTile,
    KpiTileContent,
    KpiValue,
    LeaderboardRow,
    MicroBar,
    PaginationFooter,
    PaginationRow,
    Panel,
    PanelTitle,
    PreviewEyeButton,
    ProgressBarFill,
    ProgressBarWrap,
    ScorePill,
    StatusFilterDropdownWrap,
    StatusQuickFilterLabel,
    StatusQuickFilterPill,
    StatusQuickFilterPills,
    StatusQuickFilterToggle,
    QuickFiltersBar,
    QuickFiltersGrid,
    QuickFiltersGridMobile,
    QuickFilterColumn,
    QuickFilterColumnMobile,
    TableWrap,
    TaskCard,
    TasksTable,
    TemplateCellInner,
    TemplateName,
    TaskTitleText,
    ViewOnlyChip,
    ViewToggle,
} from './task-dashboard-styles'

const STATUS_OPTIONS = DASHBOARD_STATUS_OPTIONS
const FILTER_DROPDOWN_ALL = 'all'

const FacetQuickFilterSection = ({
    label,
    options,
    activeValue,
    expanded,
    onToggleExpanded,
    onSelect,
    filtersLoading,
    Column = QuickFilterColumn,
}) => {
    const { visible, hasMore, hiddenCount } = getVisibleFacetQuickFilterOptions(
        options,
        { expanded, activeValue },
    )

    if (!options.length) {
        return null
    }

    return (
        <Column>
            <StatusQuickFilterLabel>{label}</StatusQuickFilterLabel>
            <StatusQuickFilterPills>
                {visible.map(opt => {
                    const isActive = activeValue === opt.value
                    return (
                        <StatusQuickFilterPill
                            key={opt.value || 'all'}
                            type="button"
                            $variant="neutral"
                            $active={isActive}
                            disabled={filtersLoading}
                            onClick={() => onSelect(opt.value)}
                        >
                            {opt.label}
                            <span className="count">({opt.count})</span>
                        </StatusQuickFilterPill>
                    )
                })}
                {(hasMore || expanded) && (
                    <StatusQuickFilterToggle
                        type="button"
                        disabled={filtersLoading}
                        onClick={onToggleExpanded}
                    >
                        {expanded
                            ? 'Show less'
                            : `Show more${hiddenCount > 0 ? ` (+${hiddenCount})` : ''}`}
                    </StatusQuickFilterToggle>
                )}
            </StatusQuickFilterPills>
        </Column>
    )
}

const toFilterDropdownValue = value => value || FILTER_DROPDOWN_ALL

const fromFilterDropdownValue = value =>
    value === FILTER_DROPDOWN_ALL ? '' : value || ''

const blurFilterDropdown = () => {
    requestAnimationFrame(() => {
        const active = document.activeElement
        if (active?.classList?.contains('search')) {
            active.blur()
        }
    })
}

// Dashboard/Tasks taskType: '' = all, Inspection, Maintenance (asset maintenance tasks, EAUDITOR-407)
const TASK_TYPE_FILTER_OPTIONS = [
    { key: 'all', value: '', text: 'All' },
    { key: 'Inspection', value: 'Inspection', text: 'Inspections' },
    { key: 'Maintenance', value: 'Maintenance', text: 'Maintenance' },
]

export const TaskDashboard = ({
    onViewReport,
    onEdit,
    onRemove,
    onReopen,
    onReject,
    templateOptions = [],
    customerOptions = [],
    customersData = [],
    customersLoading = false,
    companyId,
    auditorOptions = [],
    taskMutationCount = 0,
    selectedIds = [],
    onSelectionChange,
    onBulkDelete,
    onTasksChange,
    onLoadingChange,
    onOpenMaintenance,
}) => {
    const user = AuthService.getUser()
    const isAdminUser = isDashboardAdmin(user)
    const [roleView, setRoleView] = useState('auditor')
    let statsScope = 'auto'
    if (isAdminUser) {
        statsScope = roleView === 'admin' ? 'tenant' : 'self'
    }
    const showAdminInsights = isAdminUser && roleView === 'admin'

    const {
        stats,
        tasks,
        totalCount,
        loading,
        filtersLoading,
        filter,
        appliedFilter,
        taskFacets,
        apiTotalCount,
        updateFilter,
        applyFilters,
        applyQuickStatusFilter,
        applyQuickAuditorFilter,
        applyQuickCustomerFilter,
        resetFilters,
        pageChanged,
        refresh,
    } = useTaskDashboard(statsScope, { companyId, customersData })

    const pageSize = appliedFilter.pageSize || 20
    const pageNumber = appliedFilter.pageNumber || 1
    const {
        getCachedPreview,
        loadFullPreview,
        loadingTaskId,
        errorTaskId,
    } = useTaskFullPreviewCache(pageNumber)

    const [applyLoading, setApplyLoading] = useState(false)
    const [approvalByAuditId, setApprovalByAuditId] = useState({})

    useEffect(() => {
        if (!filtersLoading) {
            setApplyLoading(false)
        }
    }, [filtersLoading])

    // Drop view-only tasks from bulk selection (cross-user read-only cannot delete).
    useEffect(() => {
        if (!onSelectionChange || !selectedIds?.length) return
        const readOnlyOnPage = (tasks || [])
            .filter(task => isTaskReadOnly(task, user))
            .map(task => task.TaskId)
        if (!readOnlyOnPage.length) return
        const next = selectedIds.filter(id => !readOnlyOnPage.includes(id))
        if (next.length !== selectedIds.length) {
            onSelectionChange(next)
        }
    }, [tasks, user, selectedIds, onSelectionChange])

    useEffect(() => {
        const auditIds = (tasks || [])
            .filter(isTaskCompleted)
            .map(task => getTaskAuditIdForApproval(task))
            .filter(id => id != null && id !== '')

        if (!auditIds.length) {
            setApprovalByAuditId({})
            return undefined
        }

        let cancelled = false
        ReportApprovalService.loadApprovalStatusesForAuditIds(auditIds)
            .then(map => {
                if (!cancelled) setApprovalByAuditId(map || {})
            })
            .catch(() => {
                if (!cancelled) setApprovalByAuditId({})
            })

        return () => {
            cancelled = true
        }
    }, [tasks, taskMutationCount])

    const isTaskReportApproved = useCallback(
        task => {
            if (getTaskIsApprovedFlag(task) === true) return true
            const auditId = getTaskAuditIdForApproval(task)
            if (auditId == null) return false
            return approvalByAuditId[String(auditId)]?.isApproved === true
        },
        [approvalByAuditId],
    )

    const handleApplyFilters = () => {
        setApplyLoading(true)
        applyFilters()
    }

    const handleStatusQuickFilter = statusValue => {
        const activeStatus =
            appliedFilter.status || DASHBOARD_STATUS_FILTER.all

        if (activeStatus === statusValue && !filtersLoading) {
            return
        }

        setApplyLoading(true)
        applyQuickStatusFilter(statusValue)
    }

    const handleAuditorQuickFilter = auditorId => {
        const activeAuditorId = appliedFilter.auditorId || ''

        if (activeAuditorId === auditorId && !filtersLoading) {
            return
        }

        setApplyLoading(true)
        applyQuickAuditorFilter(auditorId)
    }

    const handleCustomerQuickFilter = customerId => {
        const activeCustomerId = appliedFilter.customerId || ''

        if (activeCustomerId === customerId && !filtersLoading) {
            return
        }

        setApplyLoading(true)
        applyQuickCustomerFilter(customerId)
    }

    const activeStatusFilter =
        appliedFilter.status || DASHBOARD_STATUS_FILTER.all

    const activeAuditorFilter = appliedFilter.auditorId || ''

    const activeCustomerFilter = appliedFilter.customerId || ''

    const refreshRef = useRef(refresh)
    refreshRef.current = refresh

    useEffect(() => {
        if (taskMutationCount > 0) {
            refreshRef.current()
        }
    }, [taskMutationCount])

    useEffect(() => {
        if (onTasksChange) {
            onTasksChange(tasks)
        }
    }, [tasks, onTasksChange])

    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(loading)
        }
    }, [loading, onLoadingChange])

    const [view, setView] = useState(DASHBOARD_VIEW.table)

    const [previewTask, setPreviewTask] = useState(null)
    const [previewAnchor, setPreviewAnchor] = useState(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewPinned, setPreviewPinned] = useState(false)
    const [showAllCreatedByFilters, setShowAllCreatedByFilters] = useState(false)
    const [showAllCustomerFilters, setShowAllCustomerFilters] = useState(false)
    const [customerSearchQuery, setCustomerSearchQuery] = useState('')
    const hidePreviewTimerRef = useRef(null)
    const cardsRef = useRef(null)
    const tableRef = useRef(null)

    const kpiTiles = buildKpiTiles(stats, showAdminInsights)
    const { entries: statusEntries, total: statusTotal } = buildStatusMixChartData(
        stats?.statusMix,
    )
    const statusQuickFilterOptions = useMemo(
        () => buildStatusQuickFilterOptions(taskFacets, apiTotalCount),
        [taskFacets, apiTotalCount],
    )
    const auditorQuickFilterOptions = useMemo(
        () => buildAuditorQuickFilterOptions(taskFacets, apiTotalCount),
        [taskFacets, apiTotalCount],
    )
    const customerQuickFilterOptions = useMemo(
        () => buildCustomerQuickFilterOptions(taskFacets, apiTotalCount),
        [taskFacets, apiTotalCount],
    )
    const createdByDropdownOptions = useMemo(
        () => [
            { key: 'all', value: FILTER_DROPDOWN_ALL, text: 'All' },
            ...auditorOptions.map(opt => ({
                key: String(opt.value),
                value: String(opt.value),
                text: opt.text,
            })),
        ],
        [auditorOptions],
    )

    const templateDropdownOptions = useMemo(
        () => [
            { key: 'all', value: FILTER_DROPDOWN_ALL, text: 'All templates' },
            ...templateOptions.map(opt => ({
                key: String(opt.value),
                value: String(opt.value),
                text: opt.text,
            })),
        ],
        [templateOptions],
    )

    const customerDropdownOptions = useMemo(
        () => [
            { key: 'all', value: FILTER_DROPDOWN_ALL, text: 'All customers' },
            ...customerOptions.map(opt => ({
                key: String(opt.value),
                value: String(opt.value),
                text: opt.text,
            })),
        ],
        [customerOptions],
    )
    const customerDropdownKey = `customer-filter-${customerOptions.length}`
    const leaderboard = stats?.leaderboard || []
    const maxTaskCount = leaderboard.reduce(
        (max, row) => Math.max(max, row.TaskCount || 0),
        1,
    )

    const showPreview = useCallback((task, anchorEl) => {
        setPreviewTask(task)
        setPreviewAnchor(anchorEl)
        setPreviewOpen(true)
        setPreviewPinned(true)
        if (task?.TaskId) {
            loadFullPreview(task.TaskId)
        }
    }, [loadFullPreview])

    const cancelHidePreview = useCallback(() => {
        if (hidePreviewTimerRef.current) {
            clearTimeout(hidePreviewTimerRef.current)
            hidePreviewTimerRef.current = null
        }
    }, [])

    const hidePreview = useCallback(() => {
        if (previewPinned) return
        cancelHidePreview()
        hidePreviewTimerRef.current = setTimeout(() => {
            setPreviewOpen(false)
            setPreviewTask(null)
            setPreviewAnchor(null)
            document.querySelectorAll('.task-dashboard-row').forEach(r =>
                r.classList.remove('row-active'),
            )
        }, 150)
    }, [previewPinned, cancelHidePreview])

    const closePreview = useCallback(() => {
        cancelHidePreview()
        setPreviewPinned(false)
        setPreviewOpen(false)
        setPreviewTask(null)
        setPreviewAnchor(null)
        document.querySelectorAll('.task-dashboard-row').forEach(r =>
            r.classList.remove('row-active'),
        )
    }, [cancelHidePreview])

    // Close report info modal when its task is deleted (or otherwise leaves the list)
    useEffect(() => {
        if (!previewOpen || !previewTask?.TaskId || loading) return
        const stillExists = tasks.some(t => t.TaskId === previewTask.TaskId)
        if (!stillExists) {
            closePreview()
        }
    }, [tasks, loading, previewOpen, previewTask, closePreview])

    const handleBulkDeleteClick = () => {
        if (
            previewOpen &&
            previewTask?.TaskId != null &&
            selectedIds.includes(previewTask.TaskId)
        ) {
            closePreview()
        }
        onBulkDelete?.()
    }

    const handleEyeClick = (e, task) => {
        e.stopPropagation()
        const anchorEl =
            e.currentTarget.closest('[data-preview-anchor]') ||
            e.currentTarget.closest('[data-taskid]') ||
            e.currentTarget.closest('tr')
        const rowEl = e.currentTarget.closest('tr')
        if (rowEl?.classList?.contains('task-dashboard-row')) {
            document.querySelectorAll('.task-dashboard-row').forEach(r =>
                r.classList.remove('row-active'),
            )
            rowEl.classList.add('row-active')
        }
        showPreview(task, anchorEl)
    }

    const renderPreviewEyeButton = task => (
        <PreviewEyeButton
            type="button"
            title="Preview task answers"
            aria-label="Preview task answers"
            onClick={e => handleEyeClick(e, task)}
        >
            <Eye size={14} weight="bold" />
        </PreviewEyeButton>
    )

    const from = totalCount ? (pageNumber - 1) * pageSize + 1 : 0
    const to = Math.min(pageNumber * pageSize, totalCount)
    const selectablePageIds = (tasks || [])
        .filter(task => !isTaskReadOnly(task, user))
        .map(task => task.TaskId)
    const allPageSelected =
        selectablePageIds.length > 0 &&
        selectablePageIds.every(id => selectedIds.includes(id))
    const somePageSelected =
        selectablePageIds.some(id => selectedIds.includes(id)) &&
        !allPageSelected

    const togglePageSelection = checked => {
        if (!onSelectionChange) return
        if (checked) {
            onSelectionChange(
                Array.from(new Set([...selectedIds, ...selectablePageIds])),
            )
        } else {
            onSelectionChange(
                selectedIds.filter(id => !selectablePageIds.includes(id)),
            )
        }
    }

    const toggleRowSelection = (taskId, checked) => {
        if (!onSelectionChange) return
        const task = (tasks || []).find(t => t.TaskId === taskId)
        if (task && isTaskReadOnly(task, user)) return
        if (checked) {
            onSelectionChange([...selectedIds, taskId])
        } else {
            onSelectionChange(selectedIds.filter(id => id !== taskId))
        }
    }

    const clearAllSelection = () => {
        if (onSelectionChange) onSelectionChange([])
    }

    const handleEdit = item => {
        if (!onEdit) return
        const original =
            tasks.find(t => t.TaskId === item.TaskId) || item
        // maintenance tasks are edited in their own detail (asset, links, priority) - not the inspection task form
        if (isMaintenanceTask(original) && onOpenMaintenance) {
            onOpenMaintenance(original.TaskId)
            return
        }
        onEdit(mapDashboardTaskForEdit(original))
    }

    const renderSelectionToolbar = () => {
        if (!tasks.length || !onSelectionChange) return null

        const selectAllCheckbox =
            selectablePageIds.length > 0 ? (
                <Checkbox
                    label={
                        allPageSelected
                            ? 'Unselect all on page'
                            : 'Select all on page'
                    }
                    checked={allPageSelected}
                    indeterminate={somePageSelected}
                    onChange={(e, { checked }) => togglePageSelection(checked)}
                />
            ) : null

        if (selectedIds.length > 0) {
            return (
                <BulkSelectionBar $between>
                    {view === DASHBOARD_VIEW.cards ? selectAllCheckbox || <span /> : <span />}
                    <div className="selection-actions">
                        <span>{selectedIds.length} tasks selected</span>
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={clearAllSelection}
                        >
                            Clear all
                        </button>
                        {onBulkDelete && (
                            <button
                                type="button"
                                className="delete-btn"
                                onClick={handleBulkDeleteClick}
                            >
                                Delete Selected
                            </button>
                        )}
                    </div>
                </BulkSelectionBar>
            )
        }

        if (view === DASHBOARD_VIEW.cards && selectAllCheckbox) {
            return (
                <BulkSelectionBar $alignStart>
                    {selectAllCheckbox}
                </BulkSelectionBar>
            )
        }

        return null
    }

    const renderCard = task => {
        const customerDisplay = getTaskCustomerDisplay(task)
        const taskViewOnly = isTaskReadOnly(task, user)

        return (
            <TaskCard
                key={task.TaskId}
                data-taskid={task.TaskId}
                className={`${
                    previewTask?.TaskId === task.TaskId && previewOpen
                        ? 'qp-anchor-active'
                        : ''
                } ${selectedIds.includes(task.TaskId) ? 'card-selected' : ''}`}
            >
                <CardBody>
                    <CardRow1>
                        <CardRowLeft>
                            {onSelectionChange && !taskViewOnly && (
                                <CardSelectWrap>
                                    <Checkbox
                                        checked={selectedIds.includes(task.TaskId)}
                                        onChange={(e, { checked }) =>
                                            toggleRowSelection(task.TaskId, checked)
                                        }
                                    />
                                </CardSelectWrap>
                            )}
                            <CardWho>
                                <Avatar>
                                    {getInitials(getTaskCreatedByName(task))}
                                </Avatar>
                                <div>
                                    <CardAuditorName>
                                        {getTaskCreatedByName(task)}
                                        {taskViewOnly && (
                                            <ViewOnlyChip>View only</ViewOnlyChip>
                                        )}
                                    </CardAuditorName>
                                </div>
                            </CardWho>
                        </CardRowLeft>
                        <TaskStatusBadge
                            task={task}
                            isReportApproved={isTaskReportApproved(task)}
                        />
                    </CardRow1>
                    <CardTemplate data-preview-anchor>
                        <TemplateName>
                            {getTaskTemplateName(task) || task.Title}
                        </TemplateName>
                        {renderPreviewEyeButton(task)}
                    </CardTemplate>
                    <MaintenanceTaskChip task={task} onOpen={onOpenMaintenance} />
                    {customerDisplay && (
                        <CardTemplateCustomer>
                            {customerDisplay.label}: {customerDisplay.value}
                        </CardTemplateCustomer>
                    )}
                    {getTaskTitleSubtitle(task) && (
                        <TaskTitleText>{getTaskTitleSubtitle(task)}</TaskTitleText>
                    )}
                    <CardMeta>
                        <span>
                            📅 {formatTaskDate(task.TaskDate || task.CreatedOn)}
                        </span>
                        {SHOW_TASK_DASHBOARD_SCORE && task.Score != null && (
                            <ScorePill $variant={getScorePillClass(task.Score)}>
                                {task.Score}%
                            </ScorePill>
                        )}
                        <span>
                            Due:{' '}
                            <DueText $state={task.DueState}>
                                {formatTaskDate(task.TaskDue)}
                            </DueText>
                        </span>
                    </CardMeta>
                    <CardProgressRow>
                        <span>{task.ProgressPct || 0}%</span>
                        <ProgressBarWrap>
                            <ProgressBarFill $pct={task.ProgressPct || 0} />
                        </ProgressBarWrap>
                    </CardProgressRow>
                </CardBody>
                <CardActions>
                    <TaskActionBoxes
                        variant="card"
                        item={task}
                        onViewReport={onViewReport}
                        onEdit={handleEdit}
                        onRemove={onRemove}
                        onReject={onReject}
                        onReopen={onReopen}
                        onClosePreview={closePreview}
                        reportApproved={isTaskReportApproved(task)}
                    />
                </CardActions>
            </TaskCard>
        )
    }

    const renderKpiTiles = RowComponent => (
        <RowComponent>
            {kpiTiles.map(tile => (
                <KpiTile key={tile.label} $accent={tile.accent}>
                    <KpiTileContent>
                        <KpiLabel>{tile.label}</KpiLabel>
                        <KpiValue>{tile.value}</KpiValue>
                        {tile.delta && (
                            <KpiDelta $up={tile.up}>{tile.delta}</KpiDelta>
                        )}
                    </KpiTileContent>
                </KpiTile>
            ))}
        </RowComponent>
    )

    const renderQuickFilters = (GridComponent, ColumnComponent) => (
        <GridComponent>
            <ColumnComponent>
                <StatusQuickFilterLabel>Filter by status</StatusQuickFilterLabel>
                <StatusQuickFilterPills>
                    {statusQuickFilterOptions.map(opt => {
                        const variant = getStatusFilterVariant(opt.value) || 'neutral'
                        const isActive = activeStatusFilter === opt.value
                        return (
                            <StatusQuickFilterPill
                                key={opt.value}
                                type="button"
                                $variant={variant}
                                $active={isActive}
                                disabled={filtersLoading}
                                onClick={() => handleStatusQuickFilter(opt.value)}
                            >
                                {opt.label}
                                <span className="count">({opt.count})</span>
                            </StatusQuickFilterPill>
                        )
                    })}
                </StatusQuickFilterPills>
            </ColumnComponent>

            <FacetQuickFilterSection
                label="Filter by created by"
                options={auditorQuickFilterOptions}
                activeValue={activeAuditorFilter}
                expanded={showAllCreatedByFilters}
                onToggleExpanded={() =>
                    setShowAllCreatedByFilters(prev => !prev)
                }
                onSelect={handleAuditorQuickFilter}
                filtersLoading={filtersLoading}
                Column={ColumnComponent}
            />

            <FacetQuickFilterSection
                label="Filter by customer"
                options={customerQuickFilterOptions}
                activeValue={activeCustomerFilter}
                expanded={showAllCustomerFilters}
                onToggleExpanded={() =>
                    setShowAllCustomerFilters(prev => !prev)
                }
                onSelect={handleCustomerQuickFilter}
                filtersLoading={filtersLoading}
                Column={ColumnComponent}
            />
        </GridComponent>
    )

    const renderAdminInsightsPanels = () => (
        <>
            <Panel>
                <PanelTitle>Status mix</PanelTitle>
                <DonutWrap>
                    <DonutChartWrap>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={statusEntries.filter(e => e.value > 0)}
                                    dataKey="value"
                                    innerRadius={42}
                                    outerRadius={60}
                                    paddingAngle={2}
                                >
                                    {statusEntries
                                        .filter(e => e.value > 0)
                                        .map(entry => (
                                            <Cell
                                                key={entry.name}
                                                fill={entry.color}
                                            />
                                        ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <DonutCenter>{statusTotal}</DonutCenter>
                    </DonutChartWrap>
                    <DonutLegend>
                        {statusEntries.map(entry => (
                            <div className="legend-row" key={entry.name}>
                                <span className="legend-label">
                                    <span
                                        className="sw"
                                        style={{ background: entry.color }}
                                    />
                                    {entry.name}
                                </span>
                                <span className="legend-pct">
                                    {statusTotal
                                        ? Math.round(
                                              (entry.value / statusTotal) * 100,
                                          )
                                        : 0}
                                    %
                                </span>
                            </div>
                        ))}
                    </DonutLegend>
                </DonutWrap>
            </Panel>
            <Panel>
                <PanelTitle>
                    Auditor leaderboard{' '}
                    <span style={{ color: '#9ca3af', fontSize: 10 }}>
                        (last 30 days)
                    </span>
                </PanelTitle>
                <LeaderboardRow style={{ fontWeight: 600, color: '#6b7280' }}>
                    <span />
                    <span className="name">Auditor</span>
                    <span />
                    {SHOW_TASK_DASHBOARD_ADMIN_SCORE && <span>Avg score</span>}
                    <span className="small">Tasks</span>
                </LeaderboardRow>
                {leaderboard.slice(0, 5).map((row, idx) => (
                    <LeaderboardRow
                        key={row.UserId ?? `lb-${idx}`}
                        $width={(row.TaskCount / maxTaskCount) * 100}
                    >
                        <span>{idx + 1}</span>
                        <span className="name">{row.UserName}</span>
                        <div className="bar-wrap">
                            <div
                                className="bar"
                                style={{
                                    width: `${(row.TaskCount / maxTaskCount) * 100}%`,
                                }}
                            />
                        </div>
                        {SHOW_TASK_DASHBOARD_ADMIN_SCORE && (
                            <span>
                                {row.AvgScore != null ? (
                                    <ScorePill
                                        $variant={getScorePillClass(row.AvgScore)}
                                    >
                                        {row.AvgScore}%
                                    </ScorePill>
                                ) : (
                                    '—'
                                )}
                            </span>
                        )}
                        <span className="small">{row.TaskCount}</span>
                    </LeaderboardRow>
                ))}
                {!leaderboard.length && (
                    <div style={{ color: '#9ca3af', fontSize: 12, padding: '8px 0' }}>
                        No leaderboard data yet.
                    </div>
                )}
            </Panel>
        </>
    )

    return (
        <DashboardShell>

            {isAdminUser && (
                <DemoBar>
                    <DemoBarLeft>
                        <span><b>View as:</b></span>
                        <RoleSegment>
                            <button
                                type="button"
                                className={roleView === 'auditor' ? 'active' : ''}
                                onClick={() => setRoleView('auditor')}
                            >
                                Non-Admin (Auditor)
                            </button>
                            <button
                                type="button"
                                className={roleView === 'admin' ? 'active' : ''}
                                onClick={() => setRoleView('admin')}
                            >
                                Admin
                            </button>
                        </RoleSegment>
                        <DemoHint>
                            Switches KPIs, charts, columns, and filters. Click the eye icon for task preview.
                        </DemoHint>
                    </DemoBarLeft>
                </DemoBar>
            )}

            <FilterBar>
                <FilterFieldsRow>
                <FilterGroup>
                    <label>Search</label>
                    <input
                        type="text"
                        placeholder="Title, template..."
                        value={filter.search}
                        onChange={e => updateFilter({ search: e.target.value })}
                    />
                </FilterGroup>
                <FilterGroup>
                    <label>Status</label>
                    <StatusFilterDropdownWrap>
                        <Dropdown
                            fluid
                            selection
                            className={`task-status-filter task-status-filter-${
                                getStatusFilterVariant(filter.status) || 'neutral'
                            }`}
                            options={STATUS_OPTIONS.map(opt => ({
                                key: opt.value,
                                value: opt.value,
                                text: opt.label,
                            }))}
                            value={filter.status || DASHBOARD_STATUS_FILTER.all}
                            onChange={(e, { value }) =>
                                updateFilter({ status: value })
                            }
                        />
                    </StatusFilterDropdownWrap>
                </FilterGroup>
                <FilterGroup>
                    <label>Created By</label>
                    <FilterDropdownWrap>
                        <Dropdown
                            fluid
                            selection
                            search
                            selectOnBlur={false}
                            className="task-filter-dropdown"
                            placeholder="All"
                            options={createdByDropdownOptions}
                            value={toFilterDropdownValue(filter.auditorId)}
                            onChange={(e, { value }) => {
                                updateFilter({
                                    auditorId: fromFilterDropdownValue(value),
                                })
                                blurFilterDropdown()
                            }}
                        />
                    </FilterDropdownWrap>
                </FilterGroup>
                {Number(companyId) !== 34930 && (
                    <FilterGroup>
                        <label>Task type</label>
                        <FilterDropdownWrap>
                            <Dropdown
                                fluid
                                selection
                                selectOnBlur={false}
                                className="task-filter-dropdown"
                                placeholder="All"
                                options={TASK_TYPE_FILTER_OPTIONS}
                                value={filter.taskType || ''}
                                onChange={(e, { value }) => {
                                    updateFilter({ taskType: value || '' })
                                    blurFilterDropdown()
                                }}
                            />
                        </FilterDropdownWrap>
                    </FilterGroup>
                )}
                <FilterGroup>
                    <label>Template</label>
                    <FilterDropdownWrap>
                        <Dropdown
                            fluid
                            selection
                            search
                            selectOnBlur={false}
                            className="task-filter-dropdown"
                            placeholder="All templates"
                            options={templateDropdownOptions}
                            value={toFilterDropdownValue(filter.templateId)}
                            onChange={(e, { value }) => {
                                updateFilter({
                                    templateId: fromFilterDropdownValue(value),
                                })
                                blurFilterDropdown()
                            }}
                        />
                    </FilterDropdownWrap>
                </FilterGroup>
                <FilterGroup>
                    <label>Customer</label>
                    <FilterDropdownWrap>
                        <Dropdown
                            key={customerDropdownKey}
                            fluid
                            selection
                            search
                            scrolling
                            selectOnBlur={false}
                            loading={customersLoading}
                            disabled={customersLoading}
                            className="task-filter-dropdown"
                            placeholder="All customers"
                            options={customerDropdownOptions}
                            searchQuery={customerSearchQuery}
                            onSearchChange={(_, { searchQuery }) =>
                                setCustomerSearchQuery(searchQuery)
                            }
                            onOpen={() => setCustomerSearchQuery('')}
                            onClose={() => setCustomerSearchQuery('')}
                            value={toFilterDropdownValue(filter.customerId)}
                            onChange={(e, { value }) => {
                                updateFilter({
                                    customerId: fromFilterDropdownValue(value),
                                })
                                setCustomerSearchQuery('')
                                blurFilterDropdown()
                            }}
                        />
                    </FilterDropdownWrap>
                </FilterGroup>
                <FilterDateRangeGroup>
                    <FilterDateGroup>
                        <label>From</label>
                        <input
                            type="date"
                            value={filter.startDate}
                            onChange={e =>
                                updateFilter({ startDate: e.target.value })
                            }
                        />
                    </FilterDateGroup>
                    <FilterDateGroup>
                        <label>To</label>
                        <input
                            type="date"
                            value={filter.endDate}
                            onChange={e =>
                                updateFilter({ endDate: e.target.value })
                            }
                        />
                    </FilterDateGroup>
                </FilterDateRangeGroup>
                <DesktopMode>
                    <FilterActionsGroup>
                        <ViewToggle>
                            <button
                                type="button"
                                className={view === DASHBOARD_VIEW.cards ? 'active' : ''}
                                onClick={() => setView(DASHBOARD_VIEW.cards)}
                            >
                                Cards
                            </button>
                            <button
                                type="button"
                                className={view === DASHBOARD_VIEW.table ? 'active' : ''}
                                onClick={() => setView(DASHBOARD_VIEW.table)}
                            >
                                Table
                            </button>
                        </ViewToggle>
                        <AppButton type="button" onClick={resetFilters}>
                            Reset
                        </AppButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={handleApplyFilters}
                            loading={applyLoading && filtersLoading}
                        >
                            Apply
                        </AppPrimaryButton>
                    </FilterActionsGroup>
                </DesktopMode>
                <MobileMode>
                    <FilterActionsGroupMobile>
                        <AppButton type="button" onClick={resetFilters}>
                            Reset
                        </AppButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={handleApplyFilters}
                            loading={applyLoading && filtersLoading}
                        >
                            Apply
                        </AppPrimaryButton>
                    </FilterActionsGroupMobile>
                </MobileMode>
                </FilterFieldsRow>
            </FilterBar>

            <DesktopMode>{renderKpiTiles(KpiRow)}</DesktopMode>
            <MobileMode>{renderKpiTiles(KpiRowMobile)}</MobileMode>

            <QuickFiltersBar>
                <DesktopMode>
                    {renderQuickFilters(QuickFiltersGrid, QuickFilterColumn)}
                </DesktopMode>
                <MobileMode>
                    {renderQuickFilters(QuickFiltersGridMobile, QuickFilterColumnMobile)}
                </MobileMode>
            </QuickFiltersBar>

            {showAdminInsights && (
                <>
                    <DesktopMode>
                        <InsightsRow>{renderAdminInsightsPanels()}</InsightsRow>
                    </DesktopMode>
                    <MobileMode>
                        <InsightsRowMobile>{renderAdminInsightsPanels()}</InsightsRowMobile>
                    </MobileMode>
                </>
            )}

            {loading && <LoaderBar content="Loading" />}

            {!loading && !tasks.length && (
                <EmptyState>No tasks found for the selected filters.</EmptyState>
            )}

            <MobileMode>
                {!loading && tasks.length > 0 && (
                    <>
                        {renderSelectionToolbar()}
                        <CardsGrid ref={cardsRef}>
                            {tasks.map(renderCard)}
                        </CardsGrid>
                    </>
                )}
            </MobileMode>

            <DesktopMode>
                {!loading && view === DASHBOARD_VIEW.cards && tasks.length > 0 && (
                    <>
                        {renderSelectionToolbar()}
                        <CardsGrid ref={cardsRef}>
                            {tasks.map(renderCard)}
                        </CardsGrid>
                    </>
                )}

                {!loading && view === DASHBOARD_VIEW.table && tasks.length > 0 && (
                <>
                    {renderSelectionToolbar()}
                    <TableWrap ref={tableRef}>
                    <TasksTable>
                        <thead>
                            <tr>
                                <th style={{ width: 50 }}>
                                    <div className="d-flex justify-content-center">
                                        {selectablePageIds.length > 0 && (
                                            <Checkbox
                                                checked={allPageSelected}
                                                indeterminate={somePageSelected}
                                                onChange={(e, { checked }) =>
                                                    togglePageSelection(checked)
                                                }
                                            />
                                        )}
                                    </div>
                                </th>
                                {showAdminInsights && <th>Auditor</th>}
                                <th>Template</th>
                                <th>Created By</th>
                                <th>Status</th>
                                <th>Progress</th>
                                {SHOW_TASK_DASHBOARD_SCORE && <th>Score</th>}
                                <th>Due</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map(task => {
                                const customerDisplay = getTaskCustomerDisplay(task)
                                const taskViewOnly = isTaskReadOnly(task, user)

                                return (
                                    <tr
                                        key={task.TaskId}
                                        className={`task-dashboard-row ${
                                            previewTask?.TaskId === task.TaskId && previewOpen
                                                ? 'row-active'
                                                : ''
                                        }`}
                                    >
                                        <td onClick={e => e.stopPropagation()}>
                                            <div className="d-flex justify-content-center">
                                                {onSelectionChange && !taskViewOnly && (
                                                    <Checkbox
                                                        checked={selectedIds.includes(
                                                            task.TaskId,
                                                        )}
                                                        onChange={(e, { checked }) =>
                                                            toggleRowSelection(
                                                                task.TaskId,
                                                                checked,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </td>
                                        {showAdminInsights && (
                                            <td>{task.AuditorName}</td>
                                        )}
                                        <td data-preview-anchor>
                                            <TemplateCellInner>
                                                <div className="template-text">
                                                    <TemplateName>
                                                        {getTaskTemplateName(task) || task.Title}
                                                    </TemplateName>
                                                    <MaintenanceTaskChip task={task} onOpen={onOpenMaintenance} />
                                                    {customerDisplay && (
                                                        <>
                                                            <br />
                                                            <span style={{ color: '#6b7280', fontSize: 11 }}>
                                                                {customerDisplay.label}: {customerDisplay.value}
                                                            </span>
                                                        </>
                                                    )}
                                                    {getTaskTitleSubtitle(task) && (
                                                        <>
                                                            <br />
                                                            <TaskTitleText>
                                                                {getTaskTitleSubtitle(task)}
                                                            </TaskTitleText>
                                                        </>
                                                    )}
                                                </div>
                                                {renderPreviewEyeButton(task)}
                                            </TemplateCellInner>
                                        </td>
                                        <td>
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                {getTaskCreatedByName(task)}
                                                {isTaskReadOnly(task, user) && (
                                                    <ViewOnlyChip>View only</ViewOnlyChip>
                                                )}
                                            </span>
                                        </td>
                                        <td>
                                            <TaskStatusBadge
                                                task={task}
                                                isReportApproved={isTaskReportApproved(
                                                    task,
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <MicroBar $pct={task.ProgressPct || 0}>
                                                <div />
                                            </MicroBar>
                                            <span style={{ fontSize: 11, color: '#6b7280' }}>
                                                {task.ProgressPct || 0}%
                                            </span>
                                        </td>
                                        {SHOW_TASK_DASHBOARD_SCORE && (
                                            <td>
                                                {task.Score != null ? (
                                                    <ScorePill $variant={getScorePillClass(task.Score)}>
                                                        {task.Score}%
                                                    </ScorePill>
                                                ) : (
                                                    <span style={{ color: '#9ca3af' }}>—</span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            <DueText $state={task.DueState}>
                                                {formatTaskDateShort(task.TaskDue)}
                                            </DueText>
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <TaskActionBoxes
                                                item={task}
                                                onViewReport={onViewReport}
                                                onEdit={handleEdit}
                                                onRemove={onRemove}
                                                onReject={onReject}
                                                onReopen={onReopen}
                                                onClosePreview={closePreview}
                                                reportApproved={isTaskReportApproved(
                                                    task,
                                                )}
                                            />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </TasksTable>
                </TableWrap>
                </>
                )}
            </DesktopMode>

            {totalCount > 0 && (
                <PaginationFooter>
                    <PaginationRow className="mt-3">
                        <span>
                            Showing {from}–{to} of {totalCount} tasks
                        </span>
                        <div className="pagination-wrap">
                            <PaginationComponent
                                pageChanged={pageChanged}
                                totalCount={totalCount}
                                activePage={pageNumber}
                                pageSize={pageSize}
                            />
                        </div>
                    </PaginationRow>
                </PaginationFooter>
            )}

            <TaskQuickPreview
                task={previewTask}
                anchorEl={previewAnchor}
                open={previewOpen}
                pinned={previewPinned}
                onPinnedChange={setPreviewPinned}
                onClose={closePreview}
                onViewReport={onViewReport}
                onHoverStart={cancelHidePreview}
                onHoverEnd={hidePreview}
                fullPreview={
                    previewTask ? getCachedPreview(previewTask.TaskId) : null
                }
                fullPreviewLoading={
                    !!previewTask && loadingTaskId === previewTask.TaskId
                }
                fullPreviewError={
                    !!previewTask && errorTaskId === previewTask.TaskId
                }
                reportApproved={
                    previewTask ? isTaskReportApproved(previewTask) : false
                }
            />
        </DashboardShell>
    )
}
